import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FormEvent,
} from "react";
import {
  ArrowLeft,
  Plus,
  Search,
  FileText,
  Folder,
  FolderPlus,
  ChevronDown,
  ChevronRight,
  MoreHorizontal,
  Trash2,
  RotateCcw,
  LayoutGrid,
  Shapes,
  Play,
  Image,
  ClipboardCheck,
  History,
  ArrowUpRight,
  X,
  Menu,
  Settings2,
  LogOut,
  Link2,
} from "lucide-react";
import { api, json, download } from "./api";
import {
  Brand,
  Empty,
  Modal,
  Spinner,
  dateLabel,
  ThemePicker,
} from "./components";
import { DocumentEditor, type EditorHandle } from "./DocumentEditor";
import { ProjectForm } from "./Projects";
import { BoardEditor } from "./BoardEditor";
import { Entities } from "./Entities";
import { SceneEditor } from "./game/SceneEditor";
import { demoScene } from "./game/defaults";
import { Versions } from "./History";
import { Playtests } from "./Playtests";
import type { Scene } from "./game/types";
import type {
  Asset,
  DocumentMeta,
  Folder as ProjectFolder,
  GameDocument,
  Project,
  User,
  Board,
  Entity,
  Category,
  Backlink,
} from "./types";
type WorkspaceMode =
  | "documents"
  | "assets"
  | "trash"
  | "board"
  | "entities"
  | "prototype"
  | "versions"
  | "playtests";

const templateChoices = [
  {
    id: "blank",
    title: "Пустой документ",
    description: "Чистый лист для новой идеи",
    symbol: "Aa",
  },
  {
    id: "concept",
    title: "Концепт игры",
    description: "Идея, аудитория и игровой цикл",
    symbol: "◈",
  },
  {
    id: "mechanic",
    title: "Описание механики",
    description: "Правила, действия и параметры",
    symbol: "◇",
  },
  {
    id: "playtest",
    title: "Запись плейтеста",
    description: "Гипотеза, наблюдения и решение",
    symbol: "✓",
  },
];
type Props = {
  project: Project;
  user: User;
  initialDocument: string | null;
  back: () => void;
  logout: () => void;
  onProject: (p: Project) => void;
  onDocument: (id: string | null) => void;
};
export function Workspace({
  project,
  user,
  initialDocument,
  back,
  logout,
  onProject,
  onDocument,
}: Props) {
  const [documents, setDocuments] = useState<DocumentMeta[]>([]),
    [folders, setFolders] = useState<ProjectFolder[]>([]),
    [assets, setAssets] = useState<Asset[]>([]),
    [deleted, setDeleted] = useState<DocumentMeta[]>([]),
    [selected, setSelected] = useState<GameDocument | null>(null),
    [mode, setMode] = useState<WorkspaceMode>(() => {
      const view = new URLSearchParams(location.search).get("view");
      return [
        "assets",
        "trash",
        "board",
        "entities",
        "prototype",
        "versions",
        "playtests",
      ].includes(view || "")
        ? (view as WorkspaceMode)
        : "documents";
    }),
    [boards, setBoards] = useState<Board[]>([]),
    [entities, setEntities] = useState<Entity[]>([]),
    [categories, setCategories] = useState<Category[]>([]),
    [deletedBoards, setDeletedBoards] = useState<Board[]>([]),
    [deletedEntities, setDeletedEntities] = useState<Entity[]>([]),
    [scenes, setScenes] = useState<Scene[]>([]),
    [deletedScenes, setDeletedScenes] = useState<Scene[]>([]),
    [sceneId, setSceneId] = useState<string | null>(
      new URLSearchParams(location.search).get("scene"),
    ),
    [boardId, setBoardId] = useState<string | null>(
      new URLSearchParams(location.search).get("board"),
    ),
    [entityId, setEntityId] = useState<string | null>(
      new URLSearchParams(location.search).get("entity"),
    ),
    [loading, setLoading] = useState(true),
    [opening, setOpening] = useState(false),
    [error, setError] = useState(""),
    [newDocument, setNewDocument] = useState(false),
    [creating, setCreating] = useState(false),
    [newFolder, setNewFolder] = useState(false),
    [folderTitle, setFolderTitle] = useState(""),
    [folderError, setFolderError] = useState(""),
    [folderBusy, setFolderBusy] = useState(false),
    [editingFolder, setEditingFolder] = useState<ProjectFolder | null>(null),
    [deleteFolder, setDeleteFolder] = useState<ProjectFolder | null>(null),
    [search, setSearch] = useState(""),
    [results, setResults] = useState<DocumentMeta[] | null>(null),
    [collapsed, setCollapsed] = useState<Set<string>>(new Set()),
    [folderMenu, setFolderMenu] = useState<string | null>(null),
    [settings, setSettings] = useState(false),
    [mobileNav, setMobileNav] = useState(false),
    [toast, setToast] = useState("");
  const [playtestScene, setPlaytestScene] = useState<string | null>(null);
  const editor = useRef<EditorHandle>(null),
    designEditor = useRef<EditorHandle>(null),
    loadSerial = useRef(0),
    initialRef = useRef(initialDocument);
  const projectPath = `/projects/${project.id}`;
  const showToast = (text: string) => {
    setToast(text);
    window.setTimeout(() => setToast(""), 3500);
  };
  const load = useCallback(async () => {
    const [
      docs,
      dirs,
      images,
      trash,
      boardList,
      entityList,
      categoryList,
      boardTrash,
      entityTrash,
      sceneList,
      sceneTrash,
    ] = await Promise.all([
      api<DocumentMeta[]>(projectPath + "/documents"),
      api<ProjectFolder[]>(projectPath + "/folders"),
      api<Asset[]>(projectPath + "/assets"),
      api<DocumentMeta[]>(projectPath + "/documents?deleted=1"),
      api<Board[]>(projectPath + "/boards"),
      api<Entity[]>(projectPath + "/entities"),
      api<Category[]>(projectPath + "/categories"),
      api<Board[]>(projectPath + "/boards?deleted=1"),
      api<Entity[]>(projectPath + "/entities?deleted=1"),
      api<Scene[]>(projectPath + "/scenes"),
      api<Scene[]>(projectPath + "/scenes?deleted=1"),
    ]);
    setDocuments(docs);
    setFolders(dirs);
    setAssets(images);
    setDeleted(trash);
    setBoards(boardList);
    setEntities(entityList);
    setCategories(categoryList);
    setDeletedBoards(boardTrash);
    setDeletedEntities(entityTrash);
    setScenes(sceneList);
    setDeletedScenes(sceneTrash);
  }, [projectPath]);
  useEffect(() => {
    let alive = true;
    load()
      .catch((e) => {
        if (alive) setError(e.message);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [load]);
  const guard = async () =>
    (!editor.current || (await editor.current.flush())) &&
    (!designEditor.current || (await designEditor.current.flush()));
  function route(view: WorkspaceMode, id?: string | null) {
    const params = new URLSearchParams({ project: project.id });
    if (view !== "documents") params.set("view", view);
    if (id && view === "board") params.set("board", id);
    if (id && view === "entities") params.set("entity", id);
    if (id && view === "prototype") params.set("scene", id);
    history.replaceState(null, "", "/?" + params);
  }
  const openDocument = useCallback(
    async (id: string, skipGuard = false) => {
      if (!skipGuard && !(await guard())) return;
      const serial = ++loadSerial.current;
      setOpening(true);
      setError("");
      try {
        const doc = await api<GameDocument>(`${projectPath}/documents/${id}`);
        if (serial !== loadSerial.current) return;
        if (doc.deletedAt) {
          setError("Документ находится в корзине.");
          return;
        }
        setSelected(doc);
        setMode("documents");
        onDocument(id);
        setMobileNav(false);
      } catch (e) {
        setError((e as Error).message);
      } finally {
        if (serial === loadSerial.current) setOpening(false);
      }
    },
    [projectPath, onDocument],
  );
  useEffect(() => {
    if (initialRef.current) {
      void openDocument(initialRef.current, true);
      initialRef.current = null;
    }
  }, [openDocument]);
  useEffect(() => {
    if (!search.trim()) {
      setResults(null);
      return;
    }
    const controller = new AbortController(),
      timer = setTimeout(() => {
        api<DocumentMeta[]>(
          `${projectPath}/documents?search=${encodeURIComponent(search)}`,
          { signal: controller.signal },
        )
          .then(setResults)
          .catch((e) => {
            if (e.name !== "AbortError") setError(e.message);
          });
      }, 250);
    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [search, projectPath, documents]);
  async function navigate(next: WorkspaceMode) {
    if (!(await guard())) return;
    loadSerial.current++;
    setSelected(null);
    setMode(next);
    onDocument(null);
    route(next);
    setMobileNav(false);
    setError("");
  }
  async function openLink(link: Backlink) {
    if (link.type === "document") {
      await openDocument(link.id);
      return;
    }
    if (!(await guard())) return;
    setSelected(null);
    setMobileNav(false);
    setMode(link.type === "board" ? "board" : "entities");
    if (link.type === "board") setBoardId(link.id);
    else setEntityId(link.id);
    route(link.type === "board" ? "board" : "entities", link.id);
  }
  async function createBoard() {
    try {
      if (!(await guard())) return;
      const b = await api<Board>(
        projectPath + "/boards",
        json("POST", {
          title: "Новая доска",
          content: {
            nodes: [],
            edges: [],
            viewport: { x: 50, y: 50, zoom: 1 },
          },
        }),
      );
      await load();
      setBoardId(b.id);
      route("board", b.id);
    } catch (e) {
      setError((e as Error).message);
    }
  }
  async function createScene(demo: boolean) {
    try {
      if (!(await guard())) return;
      let entity = entities.find((e) => e.title === "Игрок");
      if (demo && !entity) {
        const speed = {
          id: crypto.randomUUID(),
          name: "Скорость",
          type: "number",
          value: 160,
        };
        entity = await api<Entity>(
          projectPath + "/entities",
          json("POST", {
            title: "Игрок",
            categoryId: categories[0].id,
            properties: [speed],
          }),
        );
      }
      const content = demo
        ? demoScene(
            entity?.id || null,
            entity?.properties.find(
              (p) => p.type === "number" && /^(скорость|speed)$/i.test(p.name),
            )?.id || null,
          )
        : { width: 1280, height: 720, objects: [], variables: [], rules: [] };
      const scene = await api<Scene>(
        projectPath + "/scenes",
        json("POST", {
          title: demo ? "Хрустальная поляна" : "Новая сцена",
          content,
        }),
      );
      await load();
      setMode("prototype");
      setSceneId(scene.id);
      route("prototype", scene.id);
    } catch (e) {
      setError((e as Error).message);
    }
  }
  async function createDocument(template: string) {
    setCreating(true);
    setError("");
    try {
      if (!(await guard())) return;
      const doc = await api<GameDocument>(
        projectPath + "/documents",
        json("POST", { template }),
      );
      await load();
      setSelected(doc);
      setMode("documents");
      onDocument(doc.id);
      setNewDocument(false);
      setMobileNav(false);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setCreating(false);
    }
  }
  async function saveFolder(e: FormEvent) {
    e.preventDefault();
    setFolderBusy(true);
    setFolderError("");
    try {
      await api(
        projectPath +
          "/folders" +
          (editingFolder ? "/" + editingFolder.id : ""),
        json(editingFolder ? "PATCH" : "POST", { title: folderTitle }),
      );
      await load();
      setNewFolder(false);
      setEditingFolder(null);
      setFolderTitle("");
    } catch (e) {
      setFolderError((e as Error).message);
    } finally {
      setFolderBusy(false);
    }
  }
  async function trash(doc: GameDocument) {
    try {
      await api(
        `${projectPath}/documents/${doc.id}/trash`,
        json("POST", { version: doc.version }),
      );
      setSelected(null);
      onDocument(null);
      await load();
      showToast("Документ перемещён в корзину.");
    } catch (e) {
      setError((e as Error).message);
    }
  }
  function saved(doc: GameDocument) {
    setDocuments((current) =>
      current.map((d) =>
        d.id === doc.id ? ({ ...doc, content: undefined } as DocumentMeta) : d,
      ),
    );
  }
  const treeDocs = results ?? documents;
  const activeBoard = boards.find((b) => b.id === boardId) || boards[0];
  const activeScene = scenes.find((s) => s.id === sceneId) || scenes[0];
  const documentButton = (d: DocumentMeta) => (
    <button
      className={`tree-document ${selected?.id === d.id ? "selected" : ""}`}
      key={d.id}
      onClick={() => void openDocument(d.id)}
      title={d.title}
    >
      <FileText size={15} />
      <span>{d.title}</span>
    </button>
  );
  return (
    <div className="workspace">
      <aside className={`workspace-sidebar ${mobileNav ? "mobile-open" : ""}`}>
        <div className="workspace-brand">
          <Brand small />
          <button
            className="icon-button mobile-only"
            aria-label="Закрыть меню"
            onClick={() => setMobileNav(false)}
          >
            <X size={18} />
          </button>
        </div>
        <button
          className="back-projects"
          onClick={async () => {
            if (await guard()) back();
          }}
        >
          <ArrowLeft size={15} />
          Все проекты
        </button>
        <button
          className={`sidebar-project ${project.color}`}
          onClick={() => setSettings(true)}
        >
          <span className="project-initial">
            {project.title.slice(0, 1).toUpperCase()}
          </span>
          <span>{project.title}</span>
          <ChevronDown size={14} />
        </button>
        <label className="search-field sidebar-search">
          <Search size={15} />
          <input
            aria-label="Поиск в документах"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Поиск в проекте…"
          />
          {search && (
            <button
              className="icon-button"
              aria-label="Сбросить поиск"
              onClick={() => setSearch("")}
            >
              <X size={13} />
            </button>
          )}
        </label>
        <div className="sidebar-navigation">
          <button
            className={`sidebar-nav ${mode === "documents" ? "active" : ""}`}
            onClick={() => void navigate("documents")}
          >
            <FileText size={17} />
            <span>Документы</span>
            <span className="nav-count">{documents.length}</span>
          </button>
          <div className="sidebar-section">
            <span>{search ? "РЕЗУЛЬТАТЫ ПОИСКА" : "МАТЕРИАЛЫ ПРОЕКТА"}</span>
            <div>
              <button
                className="icon-button"
                disabled={project.archived}
                aria-label="Добавить папку"
                title="Новая папка"
                onClick={() => {
                  setFolderTitle("");
                  setFolderError("");
                  setNewFolder(true);
                }}
              >
                <FolderPlus size={15} />
              </button>
              <button
                className="icon-button"
                disabled={project.archived}
                aria-label="Добавить документ"
                title="Новый документ"
                onClick={() => setNewDocument(true)}
              >
                <Plus size={16} />
              </button>
            </div>
          </div>
          <div className="tree">
            {treeDocs.filter((d) => !d.folderId).map(documentButton)}
            {folders
              .filter(
                (f) => !search || treeDocs.some((d) => d.folderId === f.id),
              )
              .map((f) => (
                <div key={f.id} className="tree-folder">
                  <div className="folder-row">
                    <button
                      className="folder-toggle"
                      aria-expanded={!collapsed.has(f.id)}
                      onClick={() =>
                        setCollapsed((current) => {
                          const next = new Set(current);
                          next.has(f.id) ? next.delete(f.id) : next.add(f.id);
                          return next;
                        })
                      }
                    >
                      {collapsed.has(f.id) ? (
                        <ChevronRight size={13} />
                      ) : (
                        <ChevronDown size={13} />
                      )}
                      <Folder size={15} />
                      <span>{f.title}</span>
                    </button>
                    <button
                      className="icon-button folder-menu-button"
                      aria-label={`Действия с папкой ${f.title}`}
                      disabled={project.archived}
                      onClick={() =>
                        setFolderMenu(folderMenu === f.id ? null : f.id)
                      }
                    >
                      <MoreHorizontal size={15} />
                    </button>
                  </div>
                  {folderMenu === f.id && (
                    <div className="folder-menu">
                      <button
                        onClick={() => {
                          setEditingFolder(f);
                          setFolderTitle(f.title);
                          setFolderError("");
                          setFolderMenu(null);
                        }}
                      >
                        Переименовать
                      </button>
                      <button
                        onClick={() => {
                          setDeleteFolder(f);
                          setFolderMenu(null);
                        }}
                      >
                        Удалить папку
                      </button>
                      <button onClick={() => setFolderMenu(null)}>
                        Закрыть
                      </button>
                    </div>
                  )}
                  {!collapsed.has(f.id) && (
                    <div className="folder-children">
                      {treeDocs
                        .filter((d) => d.folderId === f.id)
                        .map(documentButton)}
                      {!treeDocs.some((d) => d.folderId === f.id) && (
                        <span className="tree-empty">Пока пусто</span>
                      )}
                    </div>
                  )}
                </div>
              ))}
            {search && !treeDocs.length && (
              <p className="tree-empty">Ничего не найдено</p>
            )}
            {!documents.length && !loading && !search && (
              <button
                className="tree-create"
                disabled={project.archived}
                onClick={() => setNewDocument(true)}
              >
                <Plus size={14} />
                Первый документ
              </button>
            )}
          </div>
          <div className="sidebar-divider" />
          <button
            className={`sidebar-nav ${mode === "board" ? "active" : ""}`}
            onClick={() => void navigate("board")}
          >
            <LayoutGrid size={17} />
            <span>Доска</span>
            <span className="nav-count">{boards.length}</span>
          </button>
          <button
            className={`sidebar-nav ${mode === "entities" ? "active" : ""}`}
            onClick={() => void navigate("entities")}
          >
            <Shapes size={17} />
            <span>Сущности</span>
            <span className="nav-count">{entities.length}</span>
          </button>
          <button
            className={`sidebar-nav ${mode === "assets" ? "active" : ""}`}
            onClick={() => void navigate("assets")}
          >
            <Image size={17} />
            <span>Изображения</span>
            <span className="nav-count">{assets.length}</span>
          </button>
          <button
            className={`sidebar-nav ${mode === "trash" ? "active" : ""}`}
            onClick={() => void navigate("trash")}
          >
            <Trash2 size={17} />
            <span>Корзина</span>
            {deleted.length +
              deletedBoards.length +
              deletedEntities.length +
              deletedScenes.length >
              0 && (
              <span className="nav-count">
                {deleted.length +
                  deletedBoards.length +
                  deletedEntities.length +
                  deletedScenes.length}
              </span>
            )}
          </button>
          <button
            className={`sidebar-nav ${mode === "prototype" ? "active" : ""}`}
            onClick={() => void navigate("prototype")}
          >
            <Play size={17} />
            <span>Прототип</span>
            <span className="nav-count">{scenes.length}</span>
          </button>
          <button
            className={`sidebar-nav ${mode === "playtests" ? "active" : ""}`}
            onClick={() => void navigate("playtests")}
          >
            <ClipboardCheck size={17} />
            <span>Плейтесты</span>
          </button>
          <button
            className={`sidebar-nav ${mode === "versions" ? "active" : ""}`}
            onClick={() => void navigate("versions")}
          >
            <History size={17} />
            <span>Версии</span>
          </button>
        </div>
        <div className="sidebar-theme">
          <ThemePicker />
        </div>
        <div className="sidebar-account">
          <span className="avatar">{user.username[0].toUpperCase()}</span>
          <div>
            <span>{user.username}</span>
            <small>Личное пространство</small>
          </div>
          <button
            className="icon-button"
            aria-label="Выйти"
            onClick={async () => {
              if (await guard()) logout();
            }}
          >
            <LogOut size={16} />
          </button>
        </div>
      </aside>
      <div className="workspace-content">
        <header className="workspace-topbar">
          <div className="breadcrumbs">
            <button
              className="icon-button mobile-only"
              aria-label="Открыть меню"
              onClick={() => setMobileNav(true)}
            >
              <Menu size={19} />
            </button>
            <span>{project.title}</span>
            <ChevronRight size={14} />
            <button onClick={() => void navigate(mode)}>
              {mode === "documents"
                ? "Документы"
                : mode === "board"
                  ? "Доска"
                  : mode === "entities"
                    ? "Сущности"
                    : mode === "prototype"
                      ? "Прототип"
                      : mode === "playtests"
                        ? "Плейтесты"
                        : mode === "versions"
                          ? "Контрольные версии"
                          : mode === "assets"
                            ? "Изображения"
                            : "Корзина"}
            </button>
            {selected && (
              <>
                <ChevronRight size={14} />
                <span className="current-crumb">
                  {documents.find((d) => d.id === selected.id)?.title ||
                    selected.title}
                </span>
              </>
            )}
          </div>
          <button
            className="icon-button"
            aria-label="Настройки проекта"
            onClick={() => setSettings(true)}
          >
            <Settings2 size={17} />
          </button>
        </header>
        {project.archived && (
          <div className="archive-banner">
            Проект в архиве. Для редактирования верни его в работу.
            <button onClick={() => setSettings(true)}>Открыть настройки</button>
          </div>
        )}
        {error && (
          <div className="workspace-error" role="alert">
            {error}
            <button
              className="icon-button"
              aria-label="Скрыть ошибку"
              onClick={() => setError("")}
            >
              <X size={15} />
            </button>
          </div>
        )}
        {loading || opening ? (
          <div className="loading">
            <Spinner />
            <span>Открываем материалы…</span>
          </div>
        ) : selected ? (
          <DocumentEditor
            key={`${selected.id}:${selected.version}`}
            ref={editor}
            initial={selected}
            folders={folders}
            readOnly={project.archived}
            onSaved={saved}
            onTrash={(doc) => void trash(doc)}
            onAsset={(asset) => setAssets((current) => [asset, ...current])}
            documents={documents}
            entities={entities}
            onOpen={(link) => void openLink(link)}
          />
        ) : mode === "board" ? (
          activeBoard ? (
            <BoardEditor
              key={activeBoard.id}
              ref={designEditor}
              initial={activeBoard}
              boards={boards}
              documents={documents}
              entities={entities}
              assets={assets}
              readOnly={project.archived}
              onSaved={(b) =>
                setBoards((current) =>
                  current.map((x) => (x.id === b.id ? b : x)),
                )
              }
              onSelect={async (b) => {
                if (await guard()) {
                  setBoardId(b.id);
                  route("board", b.id);
                }
              }}
              onReload={load}
              onOpen={(link) => void openLink(link)}
              onAsset={(a) => setAssets((current) => [a, ...current])}
            />
          ) : (
            <Empty
              title="Здесь появятся доски проекта"
              action={
                <button
                  className="primary"
                  disabled={project.archived}
                  onClick={() => void createBoard()}
                >
                  <Plus size={16} />
                  Создать доску
                </button>
              }
            >
              Собери материалы и связи на общем холсте.
            </Empty>
          )
        ) : mode === "entities" ? (
          <Entities
            key={entityId || "all"}
            ref={designEditor}
            projectId={project.id}
            entities={entities}
            categories={categories}
            documents={documents}
            assets={assets}
            readOnly={project.archived}
            initialId={entityId}
            onSaved={(e) =>
              setEntities((current) =>
                current.map((x) => (x.id === e.id ? e : x)),
              )
            }
            onReload={load}
            onOpen={(link) => void openLink(link)}
            onSelection={(id) => {
              route("entities", id);
            }}
            onAsset={(a) => setAssets((current) => [a, ...current])}
          />
        ) : mode === "prototype" ? (
          activeScene ? (
            <SceneEditor
              key={activeScene.id}
              ref={designEditor}
              initial={activeScene}
              scenes={scenes}
              entities={entities}
              assets={assets}
              readOnly={project.archived}
              onSaved={(s) =>
                setScenes((list) => list.map((x) => (x.id === s.id ? s : x)))
              }
              onSelect={async (s) => {
                if (await guard()) {
                  setSceneId(s.id);
                  route("prototype", s.id);
                }
              }}
              onCreate={(demo) => void createScene(demo)}
              onReload={load}
              onAsset={(a) => setAssets((current) => [a, ...current])}
              onCreatePlaytest={(scene) => {
                setPlaytestScene(scene.id);
                setMode("playtests");
                setSelected(null);
                route("playtests");
              }}
            />
          ) : (
            <Empty
              title="Проверь механику в игре"
              action={
                <div className="game-empty-actions">
                  <button
                    className="primary"
                    disabled={project.archived}
                    onClick={() => void createScene(true)}
                  >
                    Пример с кристаллами
                  </button>
                  <button
                    className="secondary"
                    disabled={project.archived}
                    onClick={() => void createScene(false)}
                  >
                    <Plus size={15} />
                    Пустая сцена
                  </button>
                </div>
              }
            >
              Собери 2D-сцену, задай управление и правила, затем запусти её
              прямо здесь.
            </Empty>
          )
        ) : mode === "versions" ? (
          <Versions
            project={project}
            onRestored={async (p) => {
              setSelected(null);
              setBoardId(null);
              setEntityId(null);
              setSceneId(null);
              await load();
              onProject(p);
              showToast(
                "Контрольная версия восстановлена. Предыдущее состояние сохранено в истории.",
              );
            }}
          />
        ) : mode === "playtests" ? (
          <Playtests
            projectId={project.id}
            readOnly={project.archived}
            scenes={scenes}
            initialSceneId={playtestScene}
            onInitialUsed={() => setPlaytestScene(null)}
          />
        ) : mode === "documents" ? (
          <div className="document-index">
            <div className="index-heading">
              <div>
                <span className="eyebrow">БИБЛИОТЕКА ПРОЕКТА</span>
                <h1>Документы</h1>
                <p className="muted">
                  Сохраняй идеи. Формулируй правила. Находи связи.
                </p>
              </div>
              <button
                className="primary"
                disabled={project.archived}
                onClick={() => setNewDocument(true)}
              >
                <Plus size={17} />
                Новый документ
              </button>
            </div>
            {documents.length ? (
              <>
                <div className="section-title">
                  <span>Последние изменения</span>
                  <span className="muted">{documents.length} документов</span>
                </div>
                <div className="document-list">
                  {documents.map((d) => (
                    <button
                      className="document-list-row"
                      key={d.id}
                      onClick={() => void openDocument(d.id)}
                    >
                      <span className={`doc-list-icon ${d.template}`}>
                        <FileText size={20} />
                      </span>
                      <span className="doc-list-copy">
                        <strong>{d.title}</strong>
                        <span>
                          {d.excerpt || "Пустой документ — готов к новой идее"}
                        </span>
                      </span>
                      <span className="doc-list-date">
                        {dateLabel(d.updatedAt)}
                      </span>
                      <ArrowUpRight size={17} />
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <div className="start-document">
                <div className="start-document-heading">
                  <span className="start-document-icon">
                    <FileText size={27} strokeWidth={1.5} />
                  </span>
                  <h2>У каждой игры есть первая страница</h2>
                  <p>Начни с чистого листа или используй готовую структуру.</p>
                </div>
                <div className="template-grid">
                  {templateChoices.map((t) => (
                    <button
                      key={t.id}
                      disabled={project.archived || creating}
                      onClick={() => void createDocument(t.id)}
                    >
                      <span className="template-symbol">{t.symbol}</span>
                      <strong>{t.title}</strong>
                      <span>{t.description}</span>
                      <ArrowUpRight size={16} />
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="project-note">
              <span className="status-dot" />
              <span>
                Изменения хранятся на сервере. Можно продолжить работу после
                перезагрузки.
              </span>
            </div>
          </div>
        ) : mode === "trash" ? (
          <div className="document-index">
            <div className="index-heading">
              <div>
                <span className="eyebrow">УДАЛЁННЫЕ МАТЕРИАЛЫ</span>
                <h1>Корзина</h1>
                <p className="muted">
                  Документы остаются здесь, пока ты не решишь их восстановить.
                </p>
              </div>
            </div>
            {deleted.length +
            deletedBoards.length +
            deletedEntities.length +
            deletedScenes.length ? (
              <div className="trash-list">
                {[
                  ...deletedBoards.map((b) => ({ ...b, kind: "boards" })),
                  ...deletedEntities.map((e) => ({ ...e, kind: "entities" })),
                  ...deletedScenes.map((s) => ({ ...s, kind: "scenes" })),
                ].map((item) => (
                  <div className="trash-row" key={item.id}>
                    {item.kind === "boards" ? (
                      <LayoutGrid size={19} />
                    ) : (
                      <Shapes size={19} />
                    )}
                    <div>
                      <strong>{item.title}</strong>
                      <span className="muted small-text">
                        {item.kind === "boards"
                          ? "Доска"
                          : item.kind === "scenes"
                            ? "Сцена"
                            : "Сущность"}{" "}
                        · удалено {dateLabel(item.deletedAt!)}
                      </span>
                    </div>
                    <button
                      className="secondary"
                      disabled={project.archived}
                      onClick={async () => {
                        try {
                          await api(
                            `${projectPath}/${item.kind}/${item.id}/trash`,
                            json("POST", {
                              version: item.version,
                              restore: true,
                            }),
                          );
                          await load();
                          showToast("Материал восстановлен.");
                        } catch (e) {
                          setError((e as Error).message);
                        }
                      }}
                    >
                      <RotateCcw size={15} />
                      Восстановить
                    </button>
                  </div>
                ))}
                {deleted.map((d) => (
                  <div className="trash-row" key={d.id}>
                    <FileText size={19} />
                    <div>
                      <strong>{d.title}</strong>
                      <span className="muted small-text">
                        Удалён {dateLabel(d.deletedAt!)}
                      </span>
                    </div>
                    <button
                      className="secondary"
                      disabled={project.archived}
                      onClick={async () => {
                        try {
                          await api(
                            `${projectPath}/documents/${d.id}/trash`,
                            json("POST", { version: d.version, restore: true }),
                          );
                          await load();
                          showToast("Документ восстановлен.");
                        } catch (e) {
                          setError((e as Error).message);
                        }
                      }}
                    >
                      <RotateCcw size={15} />
                      Восстановить
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <Empty title="В корзине ничего нет">
                Удалённые документы можно будет восстановить здесь.
              </Empty>
            )}
          </div>
        ) : (
          <div className="document-index">
            <div className="index-heading">
              <div>
                <span className="eyebrow">МАТЕРИАЛЫ ПРОЕКТА</span>
                <h1>Изображения</h1>
                <p className="muted">
                  Изображения в документах, сущностях и на досках проекта.
                </p>
              </div>
            </div>
            {assets.length ? (
              <div className="asset-grid">
                {assets.map((a) => (
                  <div className="asset-card" key={a.id}>
                    <a
                      href={`${projectPath.replace("/projects", "/api/projects")}/assets/${a.id}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <img
                        src={`/api${projectPath}/assets/${a.id}`}
                        alt={a.name}
                      />
                    </a>
                    <div>
                      <strong>{a.name}</strong>
                      <span>{Math.round(a.size / 1024)} КБ</span>
                    </div>
                    <button
                      className="text-button"
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(
                            window.location.origin +
                              `/api${projectPath}/assets/${a.id}`,
                          );
                          showToast("Адрес изображения скопирован.");
                        } catch {
                          download(
                            a.name + "-link.txt",
                            window.location.origin +
                              `/api${projectPath}/assets/${a.id}`,
                          );
                        }
                      }}
                    >
                      <Link2 size={14} />
                      Копировать адрес
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <Empty title="Здесь будут твои референсы">
                Открой документ и нажми «Вставить изображение». Поддерживаются
                PNG, JPEG и WebP до 20 МБ.
              </Empty>
            )}
          </div>
        )}
      </div>
      {newDocument && (
        <Modal title="Создать документ" close={() => setNewDocument(false)}>
          <div className="template-modal">
            {templateChoices.map((t) => (
              <button
                key={t.id}
                disabled={creating}
                onClick={() => void createDocument(t.id)}
              >
                <span className="template-symbol">{t.symbol}</span>
                <div>
                  <strong>{t.title}</strong>
                  <span>{t.description}</span>
                </div>
                {creating ? <Spinner /> : <ArrowUpRight size={17} />}
              </button>
            ))}
          </div>
        </Modal>
      )}
      {(newFolder || editingFolder) && (
        <Modal
          title={editingFolder ? "Переименовать папку" : "Новая папка"}
          close={() => {
            setNewFolder(false);
            setEditingFolder(null);
          }}
        >
          <form onSubmit={saveFolder}>
            <label>
              Название папки
              <input
                autoFocus
                required
                maxLength={160}
                value={folderTitle}
                onChange={(e) => setFolderTitle(e.target.value)}
                placeholder="Например, Механики"
              />
            </label>
            {folderError && (
              <div role="alert" className="error-message">
                {folderError}
              </div>
            )}
            <div className="modal-actions">
              <button
                type="button"
                className="secondary"
                onClick={() => {
                  setNewFolder(false);
                  setEditingFolder(null);
                }}
              >
                Отмена
              </button>
              <button
                className="primary"
                disabled={folderBusy || !folderTitle.trim()}
              >
                {folderBusy ? <Spinner /> : "Сохранить"}
              </button>
            </div>
          </form>
        </Modal>
      )}
      {deleteFolder && (
        <Modal title="Удалить папку?" close={() => setDeleteFolder(null)}>
          <p>
            Документы из папки «{deleteFolder.title}» останутся в проекте без
            папки.
          </p>
          <div className="modal-actions">
            <button className="secondary" onClick={() => setDeleteFolder(null)}>
              Отмена
            </button>
            <button
              className="danger"
              onClick={async () => {
                try {
                  if (!(await guard())) return;
                  await api(`${projectPath}/folders/${deleteFolder.id}`, {
                    method: "DELETE",
                    headers: { "Content-Type": "application/json" },
                  });
                  setDeleteFolder(null);
                  await load();
                  if (selected) await openDocument(selected.id, true);
                } catch (e) {
                  setError((e as Error).message);
                }
              }}
            >
              Удалить папку
            </button>
          </div>
        </Modal>
      )}
      {settings && (
        <ProjectForm
          initial={project}
          close={() => setSettings(false)}
          save={async (input) => {
            if (!(await guard()))
              throw new Error("Сначала сохрани изменения документа.");
            const p = await api<Project>(
              projectPath,
              json("PATCH", {
                ...input,
                version: project.version,
                archived: project.archived,
              }),
            );
            onProject(p);
          }}
        />
      )}
      {toast && (
        <div className="toast" role="status">
          {toast}
        </div>
      )}
    </div>
  );
}
